import { legacyFiltersByFilterTypeGetV2 } from "@btnet/services-btnet/handlers/legacy-filters/legacy-filters";
import { ResultFilterType } from "@btnet/services-btnet/handlers/models/resultFilterType";

import { queryClient } from "helpers/queryClient";

import { APIHandler } from "utilities/apiHandler";

import { FilterEntity, FilterEntityType } from "./Filter.api.types";

// Checked mapping so a divergence between the two enums becomes a compile error.
const cacheableResultFilterType: Partial<Record<FilterEntityType, ResultFilterType>> = {
    [FilterEntityType.MediaFolderDetails]: ResultFilterType.MediaFolderDetails,
};

export interface IFilterHandler {
    get(id: FilterEntityType, isBTAdmin?: boolean, jobID?: number | null): Promise<FilterEntity>;
}

// Filter types whose response is safe to cache across in-list navigation.
//
// jobId is intentionally omitted from the query key (server does not build from the jobsite),
// so switching jobs reuses the cached response. Current consumers are apply-only; a future page
// that adds a type here AND lets users save filters must evict the ["filters", ...] queries after
// a save (queryClient.removeQueries), otherwise the next load serves a stale cached entity.
const cacheableFilterTypes = new Set<FilterEntityType>([FilterEntityType.MediaFolderDetails]);

const filterCacheMs = 5 * 60 * 1000;

const buildFilterQueryKey = (id: FilterEntityType) => ["filters", id] as const;

function fetchApixFilters(id: FilterEntityType): Promise<object> {
    const resultType = cacheableResultFilterType[id];
    if (resultType === undefined) {
        return Promise.reject(new Error(`No APIX mapping for FilterEntityType ${id}`));
    }
    return legacyFiltersByFilterTypeGetV2(resultType).then((response) => response?.data ?? {});
}

function fetchLegacyFilters(
    id: FilterEntityType,
    isBTAdmin?: boolean,
    jobID?: number | null,
): Promise<object> {
    const data = {
        jobID: jobID,
        useSession: jobID === undefined,
    };
    return APIHandler(`/api/Filters/${id}`, {
        method: "GET",
        responseType: (raw: object): object => raw,
        isBTAdmin,
        data: data,
    });
}

export class FilterHandler implements IFilterHandler {
    async get(
        id: FilterEntityType,
        isBTAdmin?: boolean,
        jobID?: number | null,
    ): Promise<FilterEntity> {
        const raw = cacheableFilterTypes.has(id)
            ? await queryClient.fetchQuery({
                  queryKey: buildFilterQueryKey(id),
                  queryFn: () => fetchApixFilters(id),
                  staleTime: filterCacheMs,
                  cacheTime: filterCacheMs,
                  retry: false,
              })
            : await fetchLegacyFilters(id, isBTAdmin, jobID);

        return new FilterEntity(raw);
    }
}

export const defaultFilterHandler = new FilterHandler();
